import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { Box, Image, Flex, Grid, GridItem } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
function isVideo(url) {
    return /\.(mp4|webm|ogg)$/i.test(url);
}
const EventVibeGallery = ({ vibeUrls, eventId, eventData, }) => {
    const navigate = useNavigate();
    const [orientation, setOrientation] = useState("unknown");
    const videoRef = useRef(null);
    // Ensure vibeUrls is a valid array
    const safeVibeUrls = Array.isArray(vibeUrls) ? vibeUrls : [];
    // Sort: video first if available
    let sortedVibeUrls = [...safeVibeUrls];
    const firstVideoIdx = sortedVibeUrls.findIndex(isVideo);
    if (firstVideoIdx > 0) {
        const [videoUrl] = sortedVibeUrls.splice(firstVideoIdx, 1);
        sortedVibeUrls.unshift(videoUrl);
    }
    const firstMediaUrl = sortedVibeUrls[0];
    // Detect orientation of the first media
    useEffect(() => {
        if (!firstMediaUrl)
            return;
        if (isVideo(firstMediaUrl)) {
            const video = document.createElement("video");
            video.src = firstMediaUrl;
            video.onloadedmetadata = () => {
                const ratio = video.videoWidth / video.videoHeight;
                setOrientation(ratio < 0.9 ? "portrait" : "landscape");
            };
            video.onerror = () => setOrientation("landscape");
        }
        else {
            const img = new window.Image();
            img.src = firstMediaUrl;
            img.onload = () => {
                const ratio = img.naturalWidth / img.naturalHeight;
                setOrientation(ratio < 0.9 ? "portrait" : "landscape");
            };
            img.onerror = () => setOrientation("landscape");
        }
    }, [firstMediaUrl]);
    if (safeVibeUrls.length === 0)
        return null;
    const handleMediaClick = (url, idx) => {
        navigate("/vibes", {
            state: {
                data: {
                    vibes: {
                        vibes: sortedVibeUrls,
                        defaultIdx: idx,
                    },
                    ...eventData,
                },
            },
        });
    };
    const gridItems = sortedVibeUrls.slice(0, 6);
    const itemCount = gridItems.length;
    const showOverlay = safeVibeUrls.length > 6;
    const isPortrait = orientation === "portrait";
    // Render main media cell
    const renderMainMedia = (url, rowSpan, colSpan, height) => {
        return (_jsx(GridItem, { rowSpan: rowSpan, colSpan: colSpan, position: "relative", children: _jsx(Box, { position: "relative", w: "100%", h: height || "100%", cursor: "pointer", onClick: () => handleMediaClick(url, 0), children: isVideo(url) ? (_jsx("video", { ref: videoRef, src: url, style: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 4,
                    }, autoPlay: true, muted: true, loop: true, preload: "metadata" })) : (_jsx(Image, { src: url, alt: "Vibe 1", objectFit: "cover", borderRadius: 4, w: "100%", h: "100%" })) }) }));
    };
    // Render overlay cell
    const renderOverlayCell = (url, idx, height) => {
        const isVid = isVideo(url);
        return (_jsxs(GridItem, { position: "relative", cursor: "pointer", onClick: () => handleMediaClick(url, 0), children: [isVid ? (_jsx(Box, { position: "relative", w: "100%", h: height, bg: "#222", children: _jsx("video", { src: url, style: {
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 4,
                            filter: "blur(2px) brightness(0.7)",
                        }, autoPlay: true, muted: true, loop: true, playsInline: true, preload: "metadata" }) })) : (_jsx(Image, { src: url, alt: `Vibe ${idx + 1}`, objectFit: "cover", borderRadius: 4, w: "100%", h: height, filter: "blur(2px) brightness(0.7)" })), _jsx(Flex, { position: "absolute", top: 0, left: 0, w: "100%", h: "100%", align: "center", justify: "center", bg: "rgba(0,0,0,0.4)", borderRadius: 4, color: "white", fontSize: "2xl", fontWeight: "bold", children: `+${safeVibeUrls.length - 6}` })] }, idx));
    };
    // Render regular thumbnail cell
    const renderThumbnailCell = (url, idx, height) => {
        const isVid = isVideo(url);
        return (_jsx(GridItem, { position: "relative", cursor: "pointer", onClick: () => handleMediaClick(url, idx), children: isVid ? (_jsx("video", { src: url, style: {
                    width: "100%",
                    height,
                    objectFit: "cover",
                    borderRadius: 4,
                }, autoPlay: true, muted: true, loop: true, playsInline: true, preload: "metadata" })) : (_jsx(Image, { src: url, alt: `Vibe ${idx + 1}`, objectFit: "cover", borderRadius: 4, w: "100%", h: height })) }, idx));
    };
    // ============ SINGLE ITEM ============
    if (itemCount === 1) {
        return (_jsx(Box, { w: "100%", minW: "180px", children: _jsx(Box, { position: "relative", w: "100%", h: isPortrait ? "256px" : "180px", cursor: "pointer", onClick: () => handleMediaClick(gridItems[0], 0), children: isVideo(gridItems[0]) ? (_jsx("video", { src: gridItems[0], style: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 4,
                    }, autoPlay: true, muted: true, loop: true, preload: "metadata" })) : (_jsx(Image, { src: gridItems[0], alt: "Vibe 1", objectFit: "cover", borderRadius: 4, w: "100%", h: "100%" })) }) }));
    }
    // ============ PORTRAIT LAYOUTS ============
    // All portrait layouts: Main spans 1 col × N rows, thumbnails fill right side
    if (isPortrait) {
        // 2 items: Main (1x2) + 1 thumb (1x2)
        if (itemCount === 2) {
            return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "1fr 1fr", templateRows: "repeat(2, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 1), renderThumbnailCell(gridItems[1], 1, "100%")] }) }));
        }
        // 3 items: Main (1x2) + 2 thumbs stacked
        if (itemCount === 3) {
            return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "1fr 1fr", templateRows: "repeat(2, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 1), renderThumbnailCell(gridItems[1], 1, "80px"), renderThumbnailCell(gridItems[2], 2, "80px")] }) }));
        }
        // 4 items: Main (1x2) + 2 thumbs on right + 2 on bottom
        if (itemCount === 4) {
            return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "1fr 1fr 1fr", templateRows: "repeat(2, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 1), renderThumbnailCell(gridItems[1], 1, "80px"), renderThumbnailCell(gridItems[2], 2, "80px"), renderThumbnailCell(gridItems[3], 3, "80px")] }) }));
        }
        // 5+ items: Main (1x3) + 5 thumbs in 2x3 grid on right
        return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "1fr 1fr 1fr", templateRows: "repeat(3, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 3, 1), renderThumbnailCell(gridItems[1], 1, "80px"), renderThumbnailCell(gridItems[2], 2, "80px"), renderThumbnailCell(gridItems[3], 3, "80px"), gridItems[4] &&
                        (showOverlay
                            ? renderOverlayCell(gridItems[4], 4, "80px")
                            : renderThumbnailCell(gridItems[4], 4, "80px")), gridItems[5] &&
                        (showOverlay
                            ? renderOverlayCell(gridItems[5], 5, "80px")
                            : renderThumbnailCell(gridItems[5], 5, "80px"))] }) }));
    }
    // ============ LANDSCAPE LAYOUTS ============
    // All landscape layouts: Main spans 2 cols × 2 rows, thumbnails fill remaining cells
    // 2 items: Main (2x2) + 1 thumb on right
    if (itemCount === 2) {
        return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "2fr 1fr", templateRows: "repeat(2, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 1), renderThumbnailCell(gridItems[1], 1, "100%")] }) }));
    }
    // 3 items: Main (2x2) + 2 thumbs stacked on right
    if (itemCount === 3) {
        return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "repeat(3, 1fr)", templateRows: "repeat(2, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 2), renderThumbnailCell(gridItems[1], 1, "80px"), renderThumbnailCell(gridItems[2], 2, "80px")] }) }));
    }
    // 4 items: Main (2x2) + 2 on right + 1 on bottom
    if (itemCount === 4) {
        return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "repeat(3, 1fr)", templateRows: "repeat(3, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 2), renderThumbnailCell(gridItems[1], 1, "80px"), renderThumbnailCell(gridItems[2], 2, "80px"), renderThumbnailCell(gridItems[3], 3, "80px")] }) }));
    }
    // 5 items: Main (2x2) + 2 on right + 2 on bottom
    if (itemCount === 5) {
        return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "repeat(3, 1fr)", templateRows: "repeat(3, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 2), renderThumbnailCell(gridItems[1], 1, "80px"), renderThumbnailCell(gridItems[2], 2, "80px"), renderThumbnailCell(gridItems[3], 3, "80px"), renderThumbnailCell(gridItems[4], 4, "80px")] }) }));
    }
    // 6+ items: Main (2x2) + 2 on right + 3 on bottom
    return (_jsx(Box, { w: "100%", minW: "180px", children: _jsxs(Grid, { templateColumns: "repeat(3, 1fr)", templateRows: "repeat(3, 80px)", gap: 3, children: [renderMainMedia(gridItems[0], 2, 2), renderThumbnailCell(gridItems[1], 1, "80px"), renderThumbnailCell(gridItems[2], 2, "80px"), renderThumbnailCell(gridItems[3], 3, "80px"), renderThumbnailCell(gridItems[4], 4, "80px"), showOverlay
                    ? renderOverlayCell(gridItems[5], 5, "80px")
                    : renderThumbnailCell(gridItems[5], 5, "80px")] }) }));
};
export default EventVibeGallery;
